import { useAppSession } from "@/auth/AppSessionContext";
import { FLAGS, FLAG_DEFAULTS } from "@/feature-flags";
import { FeatureHubGuard } from "@/featureFlags";
import { MeteomaticsLogo } from "@/meteomatics-logo";
import { useChangelogPopup } from "@/overlay/components/WhatsNewWindow/useChangelogPopup";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { mapLocaleToContentLanguage } from "@/repository/mapper/LanguageMapper";
import { pageMarketplace, pageProfiles, startsWithExtraPath } from "@/route";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Tabset } from "@mm/metx-design-system";
import { useFlag } from "@openfeature/react-sdk";
import { type ComponentType, type ReactNode, Suspense, lazy, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import logo from "../logo-ncm.png";
import "./profile.scss";

type OverlayModule = {
  ProfileOverlay: ComponentType<any>;
};

export type ProfileAreaTab = "profiles" | "marketplace";

type ProfileAreaLayoutProps = {
  activeTab: ProfileAreaTab;
  showCustomLogo?: boolean;
  children?: ReactNode;
};

export function ProfileAreaLayout({ activeTab, showCustomLogo, children }: ProfileAreaLayoutProps) {
  const { sessionActivated } = useAppSession();
  const desktop = useDesktop();
  const { i18n } = useLingui();
  const history = useHistory();
  const location = useLocation();
  const { value: isChangelogActive } = useFlag("changelog", false);
  const { value: currentChangelogVersion } = useFlag(
    FLAGS.CURRENT_CHANGELOG_VERSION,
    FLAG_DEFAULTS[FLAGS.CURRENT_CHANGELOG_VERSION],
  );

  const contentLanguage = mapLocaleToContentLanguage(i18n.locale);
  const popup = useChangelogPopup(contentLanguage, currentChangelogVersion, 3);
  const whatsNewWindowId = instanceId(WindowKind.WhatsNewWindow);
  const desktopDispatch = desktop.dispatch;
  const routePrefix = startsWithExtraPath(location.pathname);

  const handleTabChange = (value: string) => {
    if (value === "profiles") {
      history.push(`${routePrefix}${pageProfiles.asString()}`);
      return;
    }
    if (value === "marketplace") {
      history.push(`${routePrefix}${pageMarketplace.asString()}`);
    }
  };

  useEffect(() => {
    if (!sessionActivated) {
      return;
    }

    if (!isChangelogActive || !popup.version || !popup.shouldShow) {
      desktopDispatch({
        type: "setWindowVisibility",
        windowId: whatsNewWindowId,
        isVisible: false,
      });
      return;
    }

    desktopDispatch({
      type: "setWindowCustomProps",
      windowId: whatsNewWindowId,
      windowKind: WindowKind.WhatsNewWindow,
      customProps: {
        title: "What's New?",
        changelogVersion: popup.version,
        onCheckboxClick: popup.dontShowAgain,
        incrementCount: popup.incrementCount,
      },
    });

    desktopDispatch({
      type: "setWindowVisibility",
      windowId: whatsNewWindowId,
      isVisible: true,
    });
  }, [sessionActivated, desktopDispatch, isChangelogActive, popup.version, popup.shouldShow, whatsNewWindowId]);

  const { ProfileOverlay } = useMemo(() => {
    const overlayModule: Promise<OverlayModule> = import(
      /* webpackChunkName: "overlay-[request]" */ "@/overlay/overlays/profile"
    );
    const errorComponentModule = {
      default: () => <div>Loading overlay failed</div>,
    };

    return {
      ProfileOverlay: lazy(() =>
        overlayModule
          .then(({ ProfileOverlay }) => ({ default: ProfileOverlay }))
          .catch(() => {
            console.error("error");
            return errorComponentModule;
          }),
      ),
    };
  }, []);

  return (
    <>
      <div className="page page--profile-area cover-window overlay">
        <div className={classNames("box-section", "background-img", showCustomLogo && "custom-logo-background")}>
          <div className="intro-watermark">
            {showCustomLogo ? <h1>Welcome to meteoKSA</h1> : <h1>Welcome to MetX</h1>}
          </div>
          {showCustomLogo ? <img height="80" src={logo} alt="logo ncm" /> : <MeteomaticsLogo />}
        </div>
        <FeatureHubGuard flag="marketplace">
          <div className="profile-area-tabs">
            <Tabset
              size="lg"
              activeValue={activeTab}
              items={[
                {
                  icon: <span className="material-symbols-outlined">home</span>,
                  label: t({
                    id: "profiles.tab.dashboards",
                    message: "My Dashboards",
                  }),
                  value: "profiles",
                },
                {
                  icon: <span className="material-symbols-outlined">storefront</span>,
                  label: t({
                    id: "profiles.tab.marketplace",
                    message: "Marketplace",
                  }),
                  value: "marketplace",
                },
              ]}
              onChange={handleTabChange}
            />
          </div>
        </FeatureHubGuard>
        <div className="box-section">
          <div className="profile-area-content profile-area-content--page">{children}</div>
        </div>
      </div>
      <div className="gui-overlay overlay__layer">
        <Suspense>
          <ProfileOverlay />
        </Suspense>
      </div>
    </>
  );
}
